/**
 * @file AuditHistoryModal.js
 * @version 1.16.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-01-26
 * @modified 2026-11-03
 * @description Modal component for displaying audit history/change log for any entity.
 *              Formatting helpers are exported as named exports for reuse by the
 *              cross-entity admin Activity dashboard.
 */

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { formatAddress, formatAddressFromFlat } from '../utils/formatAddress';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity, formatCount } from '../utils/numberFormatters';
import './AuditHistoryModal.css';
import SentEmailModal from './SentEmailModal';
// 1.12.0 (Cam, 21 Oct 2026): every field that is not an { old, new } pair —
// Pricing & Costing's confirmed lines, rep replies, price requests, the cost
// sent to Xero — is read by utils/auditDetails instead of showing an empty row.
import { describeChanges, emailIdOf } from '../utils/auditDetails';
// 1.15.0 (Cam, 30 Oct 2026): PREVIOUS_ITEM_REMOVED / _RESTORED — an item taken
// off (or put back on) a contact's Recently ordered / Recently bought list.
// 1.16.0 (Cam, 3 Nov 2026 — SO-D10696): PO_ARCHIVED / PO_RESTORED — on the
// customer order, when its PO is archived (or restored) on its own.

// Format a phone number from its three components (type/country code, area
// code, number) the same way ContactModal does — so the audit log reads the
// way the rest of the UI does. countryCode here often holds a phone-type
// label like "Office" rather than a calling code; that's how the contact
// data is shaped in this system, so we surface it as-is.
function formatPhoneDisplay(number, countryCode, areaCode) {
  const parts = [];
  if (countryCode) {
    const stripped = String(countryCode).replace(/[\s+]/g, '');
    if (/^\d{1,2}$/.test(stripped)) parts.push(`(+${stripped})`);
    else parts.push(String(countryCode));
  }
  if (areaCode) parts.push(String(areaCode));
  if (number) parts.push(String(number));
  return parts.join(' ').trim();
}

// Field-group definitions. When ≥1 sub-field appears in a changes object we
// render the group as a single combined row instead of one row per sub-field.
// The formatter receives an object of available sub-field values; any that
// weren't in the changes payload are simply absent — we never invent values.
const FIELD_GROUPS = [
  {
    label: 'Phone',
    fields: ['phone_country_code', 'phone_area_code', 'phone'],
    format: (parts) => formatPhoneDisplay(parts.phone, parts.phone_country_code, parts.phone_area_code)
  },
  {
    label: 'Mobile',
    fields: ['mobile_country_code', 'mobile_area_code', 'mobile'],
    format: (parts) => formatPhoneDisplay(parts.mobile, parts.mobile_country_code, parts.mobile_area_code)
  },
  {
    label: 'Delivery Address',
    fields: ['delivery_attention', 'delivery_line1', 'delivery_line2', 'delivery_line3',
             'delivery_line4', 'delivery_city', 'delivery_region', 'delivery_postcode', 'delivery_country'],
    format: (parts) => {
      // Re-shape the flat-prefixed parts into the form formatAddressFromFlat expects.
      const obj = {};
      for (const [k, v] of Object.entries(parts)) obj[k] = v;
      return formatAddressFromFlat(obj, 'delivery_', { separator: '\n', includeAttention: true });
    },
    multiLine: true
  },
  {
    label: 'Invoice Address',
    fields: ['invoice_attention', 'invoice_line1', 'invoice_line2', 'invoice_line3',
             'invoice_line4', 'invoice_city', 'invoice_region', 'invoice_postcode', 'invoice_country'],
    format: (parts) => {
      const obj = {};
      for (const [k, v] of Object.entries(parts)) obj[k] = v;
      return formatAddressFromFlat(obj, 'invoice_', { separator: '\n', includeAttention: true });
    },
    multiLine: true
  }
];

// Compact summary of a contract_pricing snapshot row. The full snapshot has
// 20+ system columns most of which (created_at, updated_by, contract_group_id,
// xero_*) aren't useful to a human auditor — surface the few that matter.
const SNAPSHOT_DISPLAY_FIELDS = ['price', 'margin', 'cost', 'quantity', 'start_date', 'end_date', 'rep_name', 'herd_type', 'delivery_terms', 'is_draft', 'is_archived', 'is_on_hold'];
function summarizeSnapshot(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const lines = [];
  for (const k of SNAPSHOT_DISPLAY_FIELDS) {
    if (value[k] === null || value[k] === undefined) continue;
    let display = value[k];
    if (typeof display === 'boolean') display = display ? 'Yes' : 'No';
    else if (k.endsWith('_date') && typeof display === 'string') {
      const d = new Date(display);
      if (!isNaN(d.getTime())) display = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    // Price and cost at 2dp minimum, up to 4 where the figure has them; margin
    // is settled money and stays at 2dp.
    else if ((k === 'price' || k === 'cost') && display != null && !isNaN(display)) {
      display = formatPrice(display);
    }
    else if (k === 'margin' && display != null && !isNaN(display)) {
      display = `£${parseFloat(display).toFixed(2)}`;
    }
    else if (k === 'quantity' && display != null && !isNaN(display)) {
      display = formatQuantity(display);
    }
    lines.push(`${formatFieldName(k)}: ${display}`);
  }
  return lines.length > 0 ? lines.join('\n') : null;
}

// True when the value looks like a Xero/JSON address blob so we render it as
// a tidy multi-line address rather than a JSON string dump.
function isAddressLike(fieldName, value) {
  if (typeof value !== 'string') return false;
  if (!fieldName) return false;
  const looksLikeAddressField = fieldName === 'address'
    || fieldName.endsWith('_address')
    || fieldName === 'supplier_address'
    || fieldName === 'delivery_address'
    || fieldName === 'invoice_address'
    || fieldName === 'mainAddress';
  if (!looksLikeAddressField) return false;
  if (!value.trim().startsWith('{')) return false;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object'
      && (parsed.addressLine1 || parsed.line1 || parsed.address_line_1 || parsed.city || parsed.postalCode);
  } catch (_) {
    return false;
  }
}

/**
 * Format a date for display
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    // Seconds: several changes land within the same minute (a board edit, a
    // resend), and the order they happened in is the point of a history.
    second: '2-digit'
  });
}

/**
 * Where in the system an entry was made: the screen (and window) recorded
 * with the request, else the route's own label (`_source`, e.g. "PO edit").
 * Entries from before 2026-09-30 have neither and show nothing.
 */
function entrySource(entry) {
  // Only text is a source. PRICE_SYNCED rows stored `_source` as an object
  // ({ price_change_id }) until 2026-10-02, and calling toLowerCase on one took
  // the whole Activity page down.
  const text = (v) => (typeof v === 'string' ? v : '');
  const screen = text(entry?.source);
  const route = text(entry?.changes?._source);
  if (screen && route && !screen.toLowerCase().includes(route.toLowerCase())) return `${screen} (${route})`;
  return screen || route || '';
}

/**
 * Format field names for display
 */
function formatFieldName(fieldName) {
  if (!fieldName) return 'Record';

  const fieldMappings = {
    'status': 'Status',
    'delivery_date': 'Delivery Date',
    'delivery_address': 'Delivery Address',
    'quantity': 'Quantity',
    'supplied_quantity': 'Supplied Qty',
    'unit_price': 'Unit Price',
    'price': 'Price',
    'total': 'Total',
    'notes': 'Notes',
    'haulier': 'Haulier',
    'vehicle': 'Vehicle',
    'scheduled_time': 'Scheduled Time',
    'line_items': 'Line Items',
    'po_status': 'PO Status',
    'is_priced': 'Pricing Status',
    'customer_notes': 'Customer Notes',
    'internal_notes': 'Internal Notes',
    'mill': 'Mill',
    'supplier': 'Supplier',
    'supplier_contact_id': 'Supplier',
    'supplier_address': 'Supplier Address',
    'supplier_email': 'Supplier Email',
    'supplier_phone': 'Supplier Phone',
    'transport_method': 'Transport Method',
    'reference': 'Reference',
    'account_id': 'Supplier',
    'contact_name': 'Supplier',
    'company_name': 'Company Name',
    'email': 'Email',
    'phone': 'Phone',
    'mobile': 'Mobile',
    'is_active': 'Active Status',
    'is_deleted': 'Deleted',
    'account_no': 'Account Number',
    'xero_contact_id': 'Xero Contact ID',
    'customer_name': 'Customer',
    'product_name': 'Product',
    'product_code': 'Product Code',
    'po_number': 'PO Number',
    'so_number': 'SO Number',
    'created': 'Created',
    'approval_status': 'Approval Status',
    'approval_note': 'Approval Note',
    'office_note': 'Office Note',
    'line_count': 'Line Count',
    'line_total': 'Line Total',
    'sub_total': 'Sub Total',
    'total_tax': 'Total Tax',
    'price_accepted': 'Price Accepted',
    'supplier_delivery_terms': 'Supplier Delivery Terms',
    'excel_agri_delivery_terms': 'Our Delivery Terms',
    // The order line's purchase wording (the PO and the supplier's bill), and
    // a PO line's own words.
    'purchase_description': 'Purchase description',
    'description': 'Description',
    'delivery_instructions': 'Delivery Instructions',
    'supplier_reference': 'Supplier Reference',
    'split_count': 'Split Count',
    'splits': 'Splits Created',
    // Contact phone/address sub-fields. These mostly get folded into a
    // grouped row by FIELD_GROUPS, but the labels are used as fallbacks
    // for partial updates (only one sub-field changed).
    'phone_country_code': 'Phone (Type)',
    'phone_area_code': 'Phone (Area Code)',
    'mobile_country_code': 'Mobile (Type)',
    'mobile_area_code': 'Mobile (Area Code)',
    'delivery_attention': 'Delivery (Attention)',
    'delivery_line1': 'Delivery (Line 1)',
    'delivery_line2': 'Delivery (Line 2)',
    'delivery_line3': 'Delivery (Line 3)',
    'delivery_line4': 'Delivery (Line 4)',
    'delivery_city': 'Delivery (City)',
    'delivery_region': 'Delivery (Region)',
    'delivery_postcode': 'Delivery (Postcode)',
    'delivery_country': 'Delivery (Country)',
    'invoice_attention': 'Invoice (Attention)',
    'invoice_line1': 'Invoice (Line 1)',
    'invoice_line2': 'Invoice (Line 2)',
    'invoice_line3': 'Invoice (Line 3)',
    'invoice_line4': 'Invoice (Line 4)',
    'invoice_city': 'Invoice (City)',
    'invoice_region': 'Invoice (Region)',
    'invoice_postcode': 'Invoice (Postcode)',
    'invoice_country': 'Invoice (Country)',
    'address': 'Address',
    'mainAddress': 'Address',
    // Invoice / credit note common fields
    'invoice_no': 'Invoice No.',
    'invoice_number': 'Invoice No.',
    'invoice_date': 'Invoice Date',
    'due_date': 'Due Date',
    'amount_paid': 'Amount Paid',
    'subtotal': 'Subtotal',
    'tax': 'Tax',
    'amount': 'Amount',
    'is_credit': 'Credit Note',
    'date': 'Date',
    // A formulation entry's date and its pricing period (routes/formulations.js)
    'iteration_date': 'Formulation date',
    'period': 'Period',
    // Till Sales / Epos integration
    'contact': 'Contact',
    'customer': 'Customer',
    'invoice': 'Invoice',
    'inventory_item': 'Inventory Item',
    'account_code': 'Account Code',
    // Contract pricing (snapshot fields surfaced via summarizeSnapshot).
    // 'price' and 'quantity' are labelled above.
    'margin': 'Margin',
    'cost': 'Cost',
    'start_date': 'Start Date',
    'end_date': 'End Date',
    'rep_name': 'Rep',
    'herd_type': 'Livestock Type',
    'delivery_terms': 'Delivery Terms',
    'is_draft': 'Draft',
    'is_archived': 'Archived',
    'is_on_hold': 'On Hold',
    'snapshot': 'Snapshot'
  };

  if (fieldMappings[fieldName]) return fieldMappings[fieldName];

  // Pattern: line_N_field_name → "Line N · Field Name"
  const lineFieldMatch = fieldName.match(/^line_(\d+)_(.+)$/);
  if (lineFieldMatch) {
    const lineNum = lineFieldMatch[1];
    const subField = lineFieldMatch[2];
    const subLabel = fieldMappings[subField] || subField.split('_').map(w =>
      w.charAt(0).toUpperCase() + w.slice(1)
    ).join(' ');
    return `Line ${lineNum} \u00B7 ${subLabel}`;
  }

  return fieldName.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}

/**
 * A field whose value is a quantity — shown to the quantity rule (2dp, more
 * when the figure has them: "2 → 2.5" reads "2.00 → 2.50"). Ordered and
 * supplied quantities, pallets, weights, tonnes, payloads. Never a price, a
 * cost or rate per tonne, a display setting or an id.
 */
function isQuantityField(fieldName) {
  const f = String(fieldName || '').toLowerCase();
  if (/price|cost|amount|per_tonne|uplift|rate|display|mode|weighted|percent|pct|_id$/.test(f)) return false;
  return /quantity|qty|pallets|weight|tonnes|tonnage|payload/.test(f);
}

/**
 * Of those, a COUNT — pallets, packs per pallet: shown only to the places it
 * has ("4", "2.5"), the quantity rule's one exception (Cam, 2026-10-08).
 */
function isCountField(fieldName) {
  const f = String(fieldName || '').toLowerCase();
  return /^pallets$|_pallets$|^pallets_|qty_per_pallet|pack_count|packs$/.test(f);
}

/**
 * Format a value for display - handles dates, booleans, objects, etc.
 */
function formatValue(value, fieldName, labels = null) {
  if (value === null || value === undefined) return <span className="null-value">-</span>;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  /*
   * An id the server could resolve — an item, an account, a haulier.
   *
   * "Product 3372 -> 4013" is a true record of what was written and no use to
   * anyone reading it. The server sends a lookup for the ids it recognised;
   * one it could not (a deleted product) is absent, and the number is shown as
   * before rather than a blank.
   */
  if (labels) {
    const label = labels[String(value)];
    if (label) return <span className="resolved-value">{label}</span>;
  }

  // JSON-stringified Xero/Excel address blobs — parse and render as a
  // tidy multi-line address. Avoids the "{addressType:STREET, addressLine1:…}"
  // dumps that audit rows inherit from the legacy `address` column on
  // customers.accounts and from supplier address columns on POs.
  if (isAddressLike(fieldName, value)) {
    try {
      const formatted = formatAddress(JSON.parse(value), { separator: '\n', includeAttention: true });
      if (formatted) {
        return <span className="audit-multiline">{formatted}</span>;
      }
    } catch (_) { /* fall through */ }
  }

  // contract_pricing GROUP_UPSERT stores the entire row as `snapshot`. Render
  // a compact summary instead of "21 fields" or a JSON dump.
  if (fieldName === 'snapshot' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const summary = summarizeSnapshot(value);
    if (summary) return <span className="audit-multiline">{summary}</span>;
  }

  // Handle date fields - format as user-friendly string
  if (typeof value === 'string' && fieldName &&
      (fieldName.includes('date') || fieldName === 'created_at' || fieldName === 'updated_at' ||
       fieldName === 'sent_at' || fieldName === 'delivered_at' || fieldName === 'acknowledged_at' ||
       fieldName === 'xero_updated')) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      // Date-only strings (YYYY-MM-DD) or timestamps at midnight (e.g. delivery_date stored as ISO)
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
      const isMidnight = date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
      const isDateField = fieldName && (fieldName.includes('delivery_date') || fieldName.includes('invoice_date') || fieldName === 'date' || fieldName === 'due_date' || fieldName === 'period_start' || fieldName === 'period_end');
      if (isDateOnly || (isMidnight && isDateField)) {
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
      // Full timestamps
      return date.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    }
  }

  // Handle numeric quantity/price fields (value may be number or numeric string from DB)
  if (fieldName && (typeof value === 'number' || (typeof value === 'string' && value !== '' && !isNaN(value)))) {
    const isQty = isQuantityField(fieldName);
    // Unit prices and costs: 2dp minimum, up to 4 where the figure has them.
    const isPrice = fieldName === 'unit_price' || fieldName === 'price' ||
      fieldName === 'cost' || fieldName === 'unit_cost' ||
      fieldName === 'unit_amount' || fieldName === 'unitAmount' ||
      fieldName.endsWith('_price');
    // Totals are settled money and stay at 2dp.
    const isTotal = fieldName === 'total' || fieldName === 'line_total' || fieldName === 'sub_total' ||
      fieldName === 'line_amount' || fieldName === 'lineAmount';
    if (isQty) return isCountField(fieldName) ? formatCount(value) : formatQuantity(value);
    if (isPrice) return formatPrice(value, { showSymbol: false });
    if (isTotal) return parseFloat(value).toFixed(2);
  }

  if (typeof value === 'object') {
    // Handle arrays and objects
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="null-value">Empty</span>;
      // Array of split summary objects
      if (fieldName === 'splits' && value[0]?.order_number) {
        return value.map((s, i) => (
          <div key={i} style={{ marginBottom: 2 }}>
            <strong>{s.order_number}</strong> — {s.customer} ({s.lines} line{s.lines !== 1 ? 's' : ''})
          </div>
        ));
      }
      // Simple array
      return value.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
    }
    // Don't dump large objects - summarize
    const keys = Object.keys(value);
    if (keys.length > 5) {
      return `${keys.length} fields`;
    }
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'string' && value.length > 100) {
    return value.substring(0, 100) + '...';
  }
  return String(value);
}

/**
 * Get action badge class and label
 */
function getActionInfo(action) {
  const actionMap = {
    'CREATE': { className: 'action-create', label: 'Created' },
    'UPDATE': { className: 'action-update', label: 'Updated' },
    'DELETE': { className: 'action-delete', label: 'Deleted' },
    'SOFT_DELETE': { className: 'action-delete', label: 'Archived' },
    'RESTORE': { className: 'action-restore', label: 'Restored' },
    'STATUS_CHANGE': { className: 'action-status', label: 'Status Changed' },
    'EMAIL_SENT': { className: 'action-email', label: 'Email Sent' },
    'LINE_UPDATE': { className: 'action-update', label: 'Line Update' },
    // An item's code changed: its lines here took the new one (2026-10-30).
    'ITEM_CODE_RENAMED': { className: 'action-update', label: 'Item Code Changed' },
    'ITEM_CODE_LINES_RENAMED': { className: 'action-update', label: 'Lines Given the New Code' },
    // The Item picker's X on a contact's Recently ordered / Recently bought
    // (Cam, 30 Oct 2026 — services/previousItemRemovalService).
    'PREVIOUS_ITEM_REMOVED': { className: 'action-delete', label: 'Removed from Recent Items' },
    'PREVIOUS_ITEM_RESTORED': { className: 'action-restore', label: 'Back on Recent Items' },
    // Pricing & Costing (Cam, 19 Oct): a cost or sale price confirmed / undone,
    // rep prices imported, approved or held, and the requests to reps.
    'PRICING_CONFIRMED': { className: 'action-status', label: 'Confirmed (Pricing & Costing)' },
    'PRICING_UNCONFIRMED': { className: 'action-restore', label: 'Confirmation Undone' },
    'REP_PRICES_IMPORTED': { className: 'action-create', label: 'Rep Prices Imported' },
    'REP_PRICE_APPROVED': { className: 'action-status', label: 'Rep Price Approved' },
    'REP_PRICE_HELD': { className: 'action-status', label: 'Rep Price Held' },
    'REP_PRICE_NOT_TAKEN': { className: 'action-restore', label: 'Rep Price Not Taken' },
    'REP_REQUEST_EXCLUDED': { className: 'action-update', label: 'Removed from Send to Reps' },
    'REP_REQUEST_RESTORED': { className: 'action-restore', label: 'Back in Send to Reps' },
    // Rep order requests (30 Oct 2026): a rep's request for an order, a
    // standing order or a change — on the request, and on what it produced.
    'OPENED': { className: 'action-status', label: 'Opened' },
    'COMPLETED': { className: 'action-create', label: 'Completed' },
    'CLOSED': { className: 'action-delete', label: 'Closed' },
    'WITHDRAWN': { className: 'action-restore', label: 'Withdrawn' },
    'REP_CHANGE_REQUESTED': { className: 'action-update', label: 'Change Requested by Rep' },
    'REP_REQUEST_COMPLETED': { className: 'action-status', label: 'Rep Request Completed' },
    'PRICE_REQUESTED': { className: 'action-email', label: 'Price Requested' },
    'INVOICE_CUSTOMER_SET': { className: 'action-update', label: 'Invoice Customer Set' },
    'LINES_INVOICED': { className: 'action-create', label: 'Lines Invoiced' },
    'CONTRACT_FROM_REP_REPLY': { className: 'action-create', label: 'Contract from Rep Reply' },
    'PRICING_UNLINKED': { className: 'action-update', label: 'Contract Link Dropped' },
    // Order lines counted against a contract (28 Oct 2026)
    'CONTRACT_ORDERS_ADDED': { className: 'action-create', label: 'Orders Added to Contract' },
    'CONTRACT_ORDERS_REMOVED': { className: 'action-update', label: 'Orders Taken off Contract' },
    'CONTRACT_LINKS_REFRESHED': { className: 'action-status', label: 'Auto-links Refreshed' },
    // Costs: the bill write-back, the Xero push and resync, posted price changes
    'PO_COSTS_FROM_BILL': { className: 'action-update', label: 'PO Costs from Bill' },
    'XERO_COST_PUSHED': { className: 'action-status', label: 'Cost Sent to Xero' },
    'XERO_COST_PUSH_FAILED': { className: 'action-delete', label: 'Cost Not Sent to Xero' },
    'XERO_COST_RESYNCED': { className: 'action-update', label: 'Cost Taken from Xero' },
    // A Xero-made PO's lines sent to Xero with the save (2026-10-22)
    'XERO_PO_SYNCED': { className: 'action-status', label: 'Sent to Xero' },
    'XERO_PO_SYNC_FAILED': { className: 'action-delete', label: 'Not Sent to Xero' },
    'XERO_VERSION_ACCEPTED': { className: 'action-update', label: "Xero's Version Accepted" },
    'XERO_VERSION_KEPT': { className: 'action-status', label: 'Our Version Kept' },
    'PRICE_SYNCED': { className: 'action-status', label: 'Price Change Posted' },
    'AUTO_POST_FAILED': { className: 'action-delete', label: 'Auto-post Failed' },
    'ORDERS_REPRICED': { className: 'action-update', label: 'Orders Repriced' },
    'ORDERS_LEFT': { className: 'action-status', label: 'Orders Left as They Are' },
    'PO_UPDATE': { className: 'action-update', label: 'PO Updated' },
    // The order's PO archived (or restored) on its own (services/poArchiveService).
    'PO_ARCHIVED': { className: 'action-delete', label: 'PO Archived' },
    'PO_RESTORED': { className: 'action-restore', label: 'PO Restored' },
    'SUPPLIED_UPDATE': { className: 'action-update', label: 'Supplied Quantities Updated' },
    'DELIVERY_UPDATE': { className: 'action-status', label: 'Delivery Updated' },
    'SPLIT': { className: 'action-status', label: 'Order Split' },
    // Credit allocation — surfaced on the invoice/credit view modal as well as
    // in the admin Activity dashboard, since both read this same log.
    'CONTRACT_SENT': { className: 'action-email', label: 'Contract Sent' },
    'CONTRACT_RECEIVED': { className: 'action-create', label: 'Contract Received' },
    'CREDIT_ALLOCATED': { className: 'action-status', label: 'Credit Allocated' },
    'CREDIT_APPLIED': { className: 'action-status', label: 'Credit Applied' },
    // "Add credit" / "Add debit" — on the invoice and on the document raised for it.
    'CREDIT_RAISED_FOR': { className: 'action-create', label: 'Credit Raised' },
    'DEBIT_RAISED_FOR': { className: 'action-create', label: 'Debit Raised' },
    'credit_note_allocation': { className: 'action-status', label: 'Credit Allocated' },
    'FLAG_ACKNOWLEDGED': { className: 'action-status', label: 'Flag Reviewed' },
    'FLAG_REINSTATED': { className: 'action-restore', label: 'Flag Reinstated' },
    // Till Sales / Epos integration
    'CONTACT_RESOLVED': { className: 'action-status', label: 'Contact Resolved' },
    'INVOICE_GENERATED': { className: 'action-create', label: 'Invoice Generated' },
    // Marked invoiced by hand in Till Sales (services/tillInvoicingService)
    'MARKED_INVOICED': { className: 'action-status', label: 'Marked Invoiced' },
    'UNMARKED_INVOICED': { className: 'action-restore', label: 'Unmarked Invoiced' },
    'INVOICE_LINKED': { className: 'action-update', label: 'Invoice Linked' },
    'INVOICE_UNLINKED': { className: 'action-update', label: 'Invoice Unlinked' },
    'INVENTORY_LINKED': { className: 'action-update', label: 'Inventory Linked' },
    'INVENTORY_UNLINKED': { className: 'action-delete', label: 'Inventory Unlinked' },
    'INVENTORY_CREATED_AND_LINKED': { className: 'action-create', label: 'Inventory Created & Linked' },
    // Generate Statements → "Hand over…" (services/statementRunWorkService.js)
    'STATEMENT_HANDED_OVER': { className: 'action-status', label: 'Handed Over' },
    // A statement's two notes (services/statementNoteService.js)
    'CUSTOMER_NOTE_UPDATED': { className: 'action-update', label: 'Customer Note Changed' },
    'OFFICE_NOTE_UPDATED': { className: 'action-update', label: 'Office Note Changed' },
    // A formulation entry moved to another date, or deleted (routes/formulations.js)
    'ITERATION_REDATED': { className: 'action-update', label: 'Entry Re-dated' },
    'ITERATION_DELETE': { className: 'action-delete', label: 'Entry Deleted' }
  };

  if (actionMap[action]) {
    return actionMap[action];
  }

  // Format unknown actions: replace underscores with spaces, title case
  const formattedLabel = action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return { className: 'action-default', label: formattedLabel };
}

/**
 * Get entity type display name
 */
function getEntityTypeLabel(entityType) {
  const typeMap = {
    'contact': 'Contact',
    'purchase_order': 'Purchase Order',
    'customer_order': 'Customer Order',
    'order_line': 'Order Line',
    'invoice': 'Invoice',
    'statement': 'Statement',
    'statement_run': 'Statement Run',
    'pricing': 'Pricing',
    'transport': 'Transport',
    'supplier_document': 'Supplier Document',
    'till_transaction': 'Till Transaction',
    'epos_product': 'Epos Product',
    'contract_pricing': 'Contract',
    'price_change': 'Price Change',
    'inventory_item': 'Inventory Item',
    'bill': 'Bill'
  };
  return typeMap[entityType] || entityType.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}

/*
 * The field-by-field view of one audit entry's changes, shared with
 * PlanHistoryModal so a change reads the same wherever it is shown.
 */
// Filter out underscore-prefixed metadata fields (used for context, not displayed)
function getVisibleChanges(changes) {
  if (!changes) return {};
  return Object.fromEntries(
    Object.entries(changes).filter(([fieldName]) => !fieldName.startsWith('_'))
  );
}

// Pull every field-group out of the changes payload into composite rows,
// returning what's left as `remaining` for the per-field renderer.
function groupChanges(visibleChanges) {
  const groupedRows = [];
  const consumed = new Set();
  for (const group of FIELD_GROUPS) {
    const present = group.fields.filter(f => visibleChanges[f] !== undefined);
    if (present.length === 0) continue;
    const oldParts = {};
    const newParts = {};
    for (const f of present) {
      oldParts[f] = visibleChanges[f].old;
      newParts[f] = visibleChanges[f].new;
      consumed.add(f);
    }
    groupedRows.push({
      key: group.fields.join('|'),
      label: group.label,
      oldDisplay: group.format(oldParts),
      newDisplay: group.format(newParts),
      multiLine: !!group.multiLine,
      partial: present.length < group.fields.length
    });
  }
  const remaining = {};
  for (const [f, c] of Object.entries(visibleChanges)) {
    if (!consumed.has(f)) remaining[f] = c;
  }
  return { groupedRows, remaining };
}

/*
 * Underscore metadata a reader needs (the reason given, a price change's
 * effective date) — kept for the details though hidden from the field count.
 */
const SHOWN_META_KEYS = ['_reason', '_effective_date', '_purchase_orders'];

/** One described row (utils/auditDetails) in the change-list markup. */
function renderDetailRow(row, resolved) {
  if (row.kind === 'list') {
    return (
      <div key={row.field} className="change-item change-item-block">
        <span className="field-name">{row.label}</span>
        <div className="field-values">
          <ul className="audit-detail-lines">
            {row.items.map((text, i) => <li key={i}>{text}</li>)}
          </ul>
        </div>
      </div>
    );
  }
  if (row.kind === 'value') {
    return (
      <div key={row.field} className="change-item">
        <span className="field-name">{row.label}</span>
        <div className="field-values">
          <span className="new-value">
            {row.text != null ? row.text : formatValue(row.value, row.field, resolved?.[row.field])}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div key={row.field} className="change-item">
      <span className="field-name">{row.label}</span>
      <div className="field-values">
        {row.hasOld && (
          <span className="old-value">{formatValue(row.old, row.field, resolved?.[row.field])}</span>
        )}
        {row.hasNew && (
          <>
            <span className="arrow">→</span>
            <span className="new-value">{formatValue(row.new, row.field, resolved?.[row.field])}</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * How many rows an entry's details show — the Activity screen's "Fields"
 * count and the history window's "n fields".
 */
function countChangeRows(changes, action, entityType) {
  if (action === 'CREATE' || action === 'DELETE') return 0;
  const { groupedRows, remaining } = groupChanges(getVisibleChanges(changes));
  const meta = Object.fromEntries(SHOWN_META_KEYS.filter(k => changes?.[k] != null).map(k => [k, changes[k]]));
  return groupedRows.length + describeChanges({ ...remaining, ...meta }, { action, entityType }).length;
}

function renderChanges(changes, action, resolved = null, entityType = '') {
  const visibleChanges = getVisibleChanges(changes);
  const meta = Object.fromEntries(SHOWN_META_KEYS.filter(k => changes?.[k] != null).map(k => [k, changes[k]]));

  if (Object.keys(visibleChanges).length === 0 && Object.keys(meta).length === 0) {
    return <span className="no-changes">No field changes recorded</span>;
  }

  // For CREATE/DELETE actions, don't show full data dumps - the header already conveys the action
  if (action === 'CREATE' || action === 'DELETE') {
    // Only show if the 'created'/'deleted' key contains a simple summary, not a full data object
    const mainKey = action === 'CREATE' ? 'created' : 'deleted';
    const mainChange = visibleChanges[mainKey];
    if (mainChange) {
      const dataValue = action === 'CREATE' ? mainChange.new : mainChange.old;
      if (dataValue && typeof dataValue === 'object' && !Array.isArray(dataValue)) {
        // Filter to only show key summary fields, not full data dump.
        // Covers customer orders, POs, invoices, credit notes, and statements.
        const summaryFields = [
          'order_number', 'purchase_order_number', 'invoice_no', 'invoice_number',
          'reference', 'supplier_reference',
          'status', 'company_name', 'contact_name', 'customer_name',
          'total', 'subtotal', 'tax', 'amount', 'amount_paid',
          'line_count',
          'invoice_date', 'due_date', 'date',
          'is_credit'
        ];
        const summaryEntries = Object.entries(dataValue).filter(([k]) => summaryFields.includes(k));
        if (summaryEntries.length > 0) {
          return (
            <div className="changes-list">
              {summaryEntries.map(([fieldName, value]) => (
                <div key={fieldName} className="change-item">
                  <span className="field-name">{formatFieldName(fieldName)}</span>
                  <div className="field-values">
                    <span className="new-value">{formatValue(value, fieldName, resolved?.[fieldName])}</span>
                  </div>
                </div>
              ))}
            </div>
          );
        }
        // No summary fields found - show nothing (the header is enough)
        return null;
      }
    }
    // For non-'created'/'deleted' keys, show them normally but filtered
    return null;
  }

  const { groupedRows, remaining } = groupChanges(visibleChanges);

  return (
    <div className="changes-list">
      {groupedRows.map((row) => (
        <div key={row.key} className={`change-item ${row.multiLine ? 'change-item-block' : ''}`}>
          <span className="field-name">
            {row.label}
            {row.partial && <span className="field-partial-tag"> (partial)</span>}
          </span>
          <div className="field-values">
            <span className="old-value">
              {row.oldDisplay
                ? <span className={row.multiLine ? 'audit-multiline' : ''}>{row.oldDisplay}</span>
                : <span className="null-value">-</span>}
            </span>
            <span className="arrow">→</span>
            <span className="new-value">
              {row.newDisplay
                ? <span className={row.multiLine ? 'audit-multiline' : ''}>{row.newDisplay}</span>
                : <span className="null-value">-</span>}
            </span>
          </div>
        </div>
      ))}
      {describeChanges({ ...remaining, ...meta }, { action, entityType, resolved, formatFieldName })
        .map(row => renderDetailRow(row, resolved))}
    </div>
  );
}


/**
 * AuditHistoryModal Component
 *
 * `entryAction` adds one button per history record, for callers that can DO
 * something with a point in an entity's past — the Blend Calculator opens the
 * recipe as it stood before a given change. It is a prop rather than a special
 * case inside this component because the history modal is shared by a dozen
 * entity types and only some of them have anything to offer:
 *
 *   entryAction = {
 *     icon: 'history',                          // material symbol
 *     label: 'Open before this',
 *     isAvailable: (entry) => boolean,          // omit = always
 *     unavailableTitle: 'Why it is disabled',
 *     onClick: (entry) => void
 *   }
 *
 * Rendered on every record, disabled with a reason where it does not apply —
 * a button that vanishes between rows makes the column ragged.
 */
function AuditHistoryModal({ isOpen, onClose, entityType, entityId, entityIdentifier, entryAction = null }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  // An EMAIL_SENT entry opens the email as it was sent (SentEmailModal).
  const [viewEmailEntry, setViewEmailEntry] = useState(null);

  // Fetch audit history when modal opens
  useEffect(() => {
    if (isOpen && entityType && entityId) {
      fetchHistory();
    }
    // fetchHistory is recreated each render; these three decide when to fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityType, entityId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/audit/history/${entityType}/${entityId}?limit=100`);
      if (!response.ok) {
        throw new Error('Failed to fetch audit history');
      }
      const data = await response.json();
      setHistory(data);
    } catch (err) {
      console.error('Error fetching audit history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleEntry = (entryId) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const title = entityIdentifier
    ? `History: ${entityIdentifier}`
    : `${getEntityTypeLabel(entityType)} History`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      className="audit-history-modal"
    >
      {loading && (
        <div className="loading-state">
          <span className="material-symbols-rounded spinning">sync</span>
          <span>Loading history...</span>
        </div>
      )}

      {error && (
        <div className="error-state">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
          <button className="btn-secondary" onClick={fetchHistory}>Retry</button>
        </div>
      )}

      {!loading && !error && history.length === 0 && (
        <div className="empty-state">
          <span className="material-symbols-rounded">history</span>
          <span>No history recorded for this {getEntityTypeLabel(entityType).toLowerCase()}</span>
        </div>
      )}

      {!loading && !error && history.length > 0 && (
        <div className="history-timeline">
          {history.map((entry, index) => {
            const actionInfo = getActionInfo(entry.action);
            const isExpanded = expandedEntries.has(entry.id);
            // For CREATE/DELETE, don't count fields as expandable - we show a concise summary or nothing
            const isCreateOrDelete = entry.action === 'CREATE' || entry.action === 'DELETE';
            const changesCount = isCreateOrDelete ? 0 : countChangeRows(entry.changes, entry.action, entry.entity_type || entityType);
            const requestEmailId = emailIdOf(entry);
            // For LINE_UPDATE, show product context in the header; for PO_UPDATE show PO number
            const productContext = entry.changes?._product || entry.changes?._po;

            return (
              <div key={entry.id} className={`timeline-entry ${isExpanded ? 'expanded' : ''}`}>
                <div className="timeline-marker">
                  <div className="marker-dot"></div>
                  {index < history.length - 1 && <div className="marker-line"></div>}
                </div>

                <div className="timeline-content">
                  <div
                    className="entry-header"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (changesCount > 0) toggleEntry(entry.id);
                    }}
                    style={{ cursor: changesCount > 0 ? 'pointer' : 'default' }}
                  >
                    <div className="entry-meta">
                      <span className={`action-badge ${actionInfo.className}`}>
                        {actionInfo.label}
                      </span>
                      <span className="entry-field-group">
                        {productContext && (
                          <span className="entry-product">{productContext}</span>
                        )}
                        {entrySource(entry) && (
                          <span className="entry-where" title={`Where in the system: ${entrySource(entry)}`}>
                            <span className="material-symbols-rounded">location_on</span>
                            {entrySource(entry)}
                          </span>
                        )}
                      </span>
                      <span className="entry-date">{formatDate(entry.created_at)}</span>
                    </div>
                    <div className="entry-user">
                      <span className="material-symbols-rounded">person</span>
                      <span>{entry.user_name || 'System'}</span>
                      {entry.user_name && (
                        <span className={`user-type-badge ${entry.is_admin ? 'admin' : 'user'}`}>
                          {entry.is_admin ? 'Admin' : 'User'}
                        </span>
                      )}
                    </div>
                    {entry.action === 'EMAIL_SENT' && (
                      <button
                        type="button"
                        className="btn-icon entry-action"
                        title="View the email as it was sent"
                        onClick={(e) => { e.stopPropagation(); setViewEmailEntry(entry.email_id ? { emailId: entry.email_id } : { changeLogId: entry.id }); }}
                      >
                        <span className="material-symbols-rounded">mail</span>
                      </button>
                    )}
                    {requestEmailId != null && (
                      <button
                        type="button"
                        className="btn-icon entry-action"
                        title="View the price request email as it was sent"
                        onClick={(e) => { e.stopPropagation(); setViewEmailEntry({ emailId: requestEmailId }); }}
                      >
                        <span className="material-symbols-rounded">mail</span>
                      </button>
                    )}
                    {entryAction && (() => {
                      const ok = entryAction.isAvailable
                        ? entryAction.isAvailable(entry) : true;
                      return (
                        <button
                          type="button"
                          className={`btn-icon entry-action${ok ? '' : ' btn-disabled'}`}
                          disabled={!ok}
                          title={ok
                            ? (entryAction.label || 'Open')
                            : (entryAction.unavailableTitle || 'Not available for this record')}
                          onClick={(e) => { e.stopPropagation(); if (ok) entryAction.onClick(entry); }}
                        >
                          <span className="material-symbols-rounded">
                            {entryAction.icon || 'restore'}
                          </span>
                        </button>
                      );
                    })()}
                    {changesCount > 0 && (
                      <div className="entry-expand">
                        <span className="changes-count">{changesCount} field{changesCount !== 1 ? 's' : ''}</span>
                        <span className="material-symbols-rounded">
                          {'expand_more'}
                        </span>
                      </div>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="entry-changes">
                      {renderChanges(entry.changes, entry.action, entry.resolved, entry.entity_type || entityType)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <SentEmailModal
        isOpen={viewEmailEntry != null}
        onClose={() => setViewEmailEntry(null)}
        emailId={viewEmailEntry?.emailId}
        changeLogId={viewEmailEntry?.changeLogId}
      />
    </Modal>
  );
}

export {
  entrySource,
  renderChanges as renderAuditChanges,
  countChangeRows,
  getVisibleChanges,
  formatDate,
  formatFieldName,
  formatValue,
  getActionInfo,
  getEntityTypeLabel
};

export default AuditHistoryModal;
